// ADR 1.10.26n §2 — `/account`, a layout route with one child per settings
// category.
//
// Every route here is lazy, the layout included: an eager `AccountLayout` would
// pull the subscription query, the billing hooks and every section into the
// entry chunk (24.9.26ag AC2). It is the one lazy layout in the table, named as
// such in `lazyRoutes.test.tsx`. This module is eager, so it imports only
// `settingsCategories.ts`, which is pure data.
//
// The six category leaves are mapped from `SETTINGS_CATEGORIES`, so a category
// cannot exist without a route; each keeps a static titled handle and
// `frame: 'wide'` (`useRouteFrame()` reads only the deepest match, so a frame on
// the layout would do nothing). An unknown slug lands back on `/account` rather
// than on the global 404: it is a mistyped place *inside* settings.

import { Navigate, type RouteObject } from 'react-router-dom'

import { SETTINGS_CATEGORIES } from '../features/account/settingsCategories'
import { page, titled } from './routeHelpers'

const categoryPage = page(() => import('../features/account/CategoryPage'), 'CategoryRoute')

export const accountRoute: RouteObject = {
  path: 'account',
  lazy: page(() => import('../features/account/AccountLayout'), 'AccountLayout'),
  children: [
    // §2 — "Account Settings", so the compact header over the list does not
    // read "Account" above a first row that also reads "Account".
    {
      index: true,
      lazy: page(() => import('../features/account/CategoryPage'), 'AccountIndex'),
      handle: { ...titled('account_settings_title'), frame: 'wide' },
    },
    ...SETTINGS_CATEGORIES.map(({ slug, titleKey }) => ({
      path: slug,
      lazy: categoryPage,
      handle: { ...titled(titleKey, '/account'), frame: 'wide' },
    })),
    // ADR 2.10.26k §2 — the drilldown Cancel moved behind: a leaf under the
    // Account category, not a seventh category. `wide` like every leaf here,
    // because the layout draws its sidebar inside the route's frame.
    {
      path: 'profile/subscription',
      lazy: page(() => import('../features/billing/ManageSubscriptionPage'), 'ManageSubscriptionRoute'),
      handle: { ...titled('manage_subscription_nav_title', '/account/profile'), frame: 'wide' },
    },
    { path: '*', element: <Navigate to="/account" replace /> },
  ],
}
